/*
 * Purpose: SEO metadata SSOT — titles, descriptions, competitor keywords, route rules
 * Last modified: 2026-06-28 | Agent
 * Completeness: 96
 */
import { COMPANY_INFO } from '@/lib/companyInfo';
import { PRODUCT_NAME, PRODUCT_TERMS } from '@/lib/productTerms';
import { APP_ROUTES } from '@/lib/appRoutes';
import { isAuthPublicPath } from '@/lib/auth-public-routes';

export const SEO_SITE = {
  name: PRODUCT_NAME,
  brand: PRODUCT_TERMS.productBrand,
  url: COMPANY_INFO.officialUrl,
  marketingUrl: COMPANY_INFO.marketingUrl,
  locale: 'en_GB',
  ogImage: `${COMPANY_INFO.officialUrl}/og-image.svg`,
  twitterCard: 'summary_large_image' as const,
} as const;

/** Primary competitor positioning — used in meta keywords and on-page copy */
export const COMPETITOR_KEYWORDS = [
  'Zapier alternative',
  'Make alternative',
  'Integromat alternative',
  'n8n alternative',
  'workflow automation platform',
  'no-code automation',
  'iPaaS alternative',
  'automate without Zapier',
  'Make.com replacement',
  'self-hosted automation alternative',
  'outcome-driven automation',
  'AI workflow automation',
] as const;

export const COMPETITOR_NAMES = ['Zapier', 'Make', 'Integromat', 'n8n'] as const;

const KEYWORDS_STRING = [
  PRODUCT_TERMS.productBrand,
  'workflow automation',
  'AutoBlock Flow',
  ...COMPETITOR_KEYWORDS,
].join(', ');

export interface SeoConfig {
  title: string;
  description: string;
  keywords?: string;
  canonical: string;
  robots: string;
  ogType: 'website' | 'article';
  includeJsonLd?: boolean;
  faq?: { question: string; answer: string }[];
}

export const HOME_FAQ: { question: string; answer: string }[] = [
  {
    question: 'Is Autoblocks a Zapier alternative?',
    answer:
      'Yes. Autoblocks.run lets you describe outcomes in plain language instead of chaining Zapier Zaps. You own the connector library and runtime — no per-task Zapier billing or vendor lock-in.',
  },
  {
    question: 'How does Autoblocks compare to Make (Integromat)?',
    answer:
      'Make focuses on visual scenarios; Autoblocks is outcome-first — AI plans blocks, dry-runs with sample data, and runs on infrastructure you control. Ideal for teams outgrowing Make complexity.',
  },
  {
    question: 'Is Autoblocks an n8n alternative?',
    answer:
      'Autoblocks offers similar ownership benefits to self-hosted n8n without maintaining nodes yourself. Connectors live in a reusable library; dry-run and human approval are built in.',
  },
  {
    question: 'Can I migrate from Zapier, Make, or n8n?',
    answer:
      'Describe your existing automation as an outcome in Compose. Autoblocks maps steps to owned connectors, surfaces credential gaps, and dry-runs before live execution.',
  },
];

export const COMPETITOR_COMPARISON = [
  {
    name: 'Zapier',
    pain: 'Per-task pricing and shallow multi-step Zaps',
    autoblocks: 'Outcome compiler + owned connector library',
  },
  {
    name: 'Make',
    pain: 'Scenario sprawl and opaque error handling',
    autoblocks: 'Mission map, monitor, debug, and dry-run first',
  },
  {
    name: 'n8n',
    pain: 'Self-host ops and custom node maintenance',
    autoblocks: 'Managed product runtime with reusable ab.* connectors',
  },
  {
    name: 'Integromat',
    pain: 'Legacy Make branding and migration friction',
    autoblocks: 'Fresh outcome-driven stack — no recipe import lock-in',
  },
] as const;

function canonicalFor(pathname: string): string {
  const clean = pathname.split('?')[0]?.split('#')[0] || '/';
  if (clean === '/') return SEO_SITE.url;
  return `${SEO_SITE.url}${clean.endsWith('/') ? clean.slice(0, -1) : clean}`;
}

function matchRoute(pathname: string): SeoConfig {
  const path = pathname.split('?')[0]?.split('#')[0] || '/';

  if (path === '/' || path === '') {
    return {
      title: `${SEO_SITE.brand} — Zapier & Make Alternative | Outcome-Driven Workflow Automation`,
      description:
        'Autoblocks.run is the outcome-driven workflow automation platform — a Zapier, Make, and n8n alternative. Describe your goal; we plan, connect, dry-run, and run on owned infrastructure.',
      keywords: KEYWORDS_STRING,
      canonical: SEO_SITE.url,
      robots: 'index, follow, max-image-preview:large, max-snippet:-1',
      ogType: 'website',
      includeJsonLd: true,
      faq: HOME_FAQ,
    };
  }

  if (path === APP_ROUTES.help || path.startsWith(`${APP_ROUTES.help}/`)) {
    return {
      title: `Help & Guides — ${SEO_SITE.name} Workflow Automation`,
      description:
        `User guides for ${SEO_SITE.brand}: compose flows, connect Gmail and APIs, dry-run vs live, and migrate from Zapier, Make, or n8n.`,
      keywords: `${SEO_SITE.brand}, help, workflow automation, Zapier migration, Make alternative`,
      canonical: canonicalFor(APP_ROUTES.help),
      robots: 'index, follow',
      ogType: 'website',
    };
  }

  if (path === APP_ROUTES.systemStatus) {
    return {
      title: `System Status — ${SEO_SITE.name} Uptime & Security`,
      description:
        `Live system health, uptime, Redis status, and security audit for ${SEO_SITE.name} — transparent monitoring for your automation platform.`,
      canonical: canonicalFor(APP_ROUTES.systemStatus),
      robots: 'index, follow',
      ogType: 'website',
    };
  }

  if (path === APP_ROUTES.pricing) {
    return {
      title: `Pricing — ${SEO_SITE.name} | Zapier & Make Alternative Plans`,
      description:
        `Simple subscription plans for ${SEO_SITE.brand} — outcome-driven workflow automation without per-task Zapier billing. Secure checkout via Stripe.`,
      keywords: `${SEO_SITE.brand}, pricing, subscription, Zapier alternative pricing, workflow automation plans`,
      canonical: canonicalFor(APP_ROUTES.pricing),
      robots: 'index, follow, max-image-preview:large',
      ogType: 'website',
    };
  }

  if (path.startsWith('/auth')) {
    return {
      title: `Sign In — ${SEO_SITE.name}`,
      description: `Passwordless sign-in to ${SEO_SITE.name} — secure workflow automation without Zapier or Make lock-in.`,
      canonical: canonicalFor(path),
      robots: 'noindex, nofollow',
      ogType: 'website',
    };
  }

  if (path.startsWith('/admin')) {
    return {
      title: `Admin — ${SEO_SITE.name}`,
      description: 'Administrator analytics and operations.',
      canonical: canonicalFor(path),
      robots: 'noindex, nofollow',
      ogType: 'website',
    };
  }

  if (path.startsWith('/dashboard')) {
    return {
      title: `Dashboard — ${SEO_SITE.name}`,
      description: 'Command center for live AutoBlock Flow runs.',
      canonical: canonicalFor(path),
      robots: 'noindex, nofollow',
      ogType: 'website',
    };
  }

  if (path.startsWith('/compose')) {
    return {
      title: `${PRODUCT_TERMS.autoBlockFlowComposer} — ${SEO_SITE.name}`,
      description:
        'Describe an outcome in plain language — AI plans your workflow without Zapier recipes or Make scenarios.',
      canonical: canonicalFor(path),
      robots: 'noindex, nofollow',
      ogType: 'website',
    };
  }

  if (path.startsWith('/flows/templates')) {
    return {
      title: `${PRODUCT_TERMS.flowTemplates} — ${SEO_SITE.name}`,
      description:
        'Clone production-ready AutoBlock Flow starters — job hunt, receipts, alerts, and more.',
      canonical: canonicalFor(path),
      robots: 'noindex, nofollow',
      ogType: 'website',
    };
  }

  if (path.startsWith('/flows')) {
    return {
      title: `Flows — ${SEO_SITE.name}`,
      description: 'Saved AutoBlock Flows and the Flow Templates library.',
      canonical: canonicalFor(path),
      robots: 'noindex, nofollow',
      ogType: 'website',
    };
  }

  if (path.startsWith('/mission')) {
    return {
      title: `${PRODUCT_TERMS.mission} — ${SEO_SITE.name}`,
      description: 'Visual flow map, scheduler, monitor, and analytics.',
      canonical: canonicalFor(path),
      robots: 'noindex, nofollow',
      ogType: 'website',
    };
  }

  if (path.startsWith('/connect')) {
    return {
      title: `Connect — ${SEO_SITE.name}`,
      description: 'Vault, onboarding, and connector library for Gmail, OpenAI, and more.',
      canonical: canonicalFor(path),
      robots: 'noindex, nofollow',
      ogType: 'website',
    };
  }

  const publicDefault = isAuthPublicPath(path);
  return {
    title: SEO_SITE.name,
    description:
      'Outcome-driven workflow automation — Zapier, Make, and n8n alternative with owned execution.',
    canonical: canonicalFor(path),
    robots: publicDefault ? 'index, follow' : 'noindex, nofollow',
    ogType: 'website',
  };
}

export function getSeoForPath(pathname: string): SeoConfig {
  return matchRoute(pathname);
}
